import { AccountSubTotal, Adjustment, Invoice, LineItem } from "../../../../../pojo/Pricing";
import { S25PricingUtil } from "../../../s25.pricing.util";
import { IPaymentDetails } from "../../../s25-payments/payment.service";

export namespace InvoiceTotals {
    export type OrgTotals = ReturnType<typeof sumSubtotals>;

    /** How much of an invoice is in view: one event's share, or every related event's. */
    export type InvoiceScope = { combineRelated: boolean; eventId: number | undefined };

    /**
     * Subtotals grouped by the org they charge. Unless related events are combined, the invoice's
     * per-event entries are narrowed to the event being priced.
     */
    export function subtotalsByOrg(invoice: Invoice | undefined, scope: InvoiceScope): Map<number, AccountSubTotal[]> {
        const map = new Map<number, AccountSubTotal[]>();

        for (const subtotal of invoice?.billing?.subtotals ?? []) {
            const accounts = scope.combineRelated
                ? (subtotal.account ?? [])
                : (subtotal.accountEvent?.filter((account) => account.eventId === scope.eventId) ?? []);

            for (const account of accounts) {
                const orgId = account.chargeToId;
                if (!orgId) continue;
                map.set(orgId, [...(map.get(orgId) ?? []), account]);
            }
        }
        return map;
    }

    export function sumSubtotals(subtotals: AccountSubTotal[]) {
        return subtotals.reduce(
            (totals, subtotal) => ({
                listPrice:
                    totals.listPrice + (subtotal.occurrenceTotalCharge ?? 0) + (subtotal.requirementsListPrice ?? 0),
                grossAdjustments: totals.grossAdjustments + (subtotal.grossAdjustments ?? 0),
                profileAdjustments: totals.profileAdjustments + (subtotal.profileAdjustments ?? 0),
                requirementsAdjustments: totals.requirementsAdjustments + (subtotal.requirementsAdjustments ?? 0),
                taxableAmount: totals.taxableAmount + (subtotal.taxableAmount ?? 0),
                tax: totals.tax + (subtotal.tax ?? 0),
                eventsTotalCharge: totals.eventsTotalCharge + (subtotal.eventsTotalCharge ?? 0),
                grandTotal: totals.grandTotal + (subtotal.grandTotal ?? 0),
            }),
            {
                listPrice: 0,
                grossAdjustments: 0,
                profileAdjustments: 0,
                requirementsAdjustments: 0,
                taxableAmount: 0,
                tax: 0,
                eventsTotalCharge: 0,
                grandTotal: 0,
            },
        );
    }

    /** The fields saying which org, event and invoice an entry is for rather than what it charges. */
    const ACCOUNT_IDENTITY: ReadonlySet<string> = new Set(["chargeToId", "eventId", "invoiceId"]);

    /**
     * Two invoice-wide entries for one org, which is what merged system sets bring back. The charges
     * add up; the invoice number is whichever entry states one, the priced event's coming first.
     */
    function foldAccounts(first: AccountSubTotal, second: AccountSubTotal): AccountSubTotal {
        const folded: Record<string, unknown> = { ...first };

        for (const [field, charge] of Object.entries(second)) {
            if (ACCOUNT_IDENTITY.has(field) || typeof charge !== "number") continue;
            folded[field] = (typeof folded[field] === "number" ? (folded[field] as number) : 0) + charge;
        }
        folded.invoiceId = first.invoiceId || second.invoiceId;

        return folded as AccountSubTotal;
    }

    /**
     * The invoice-wide subtotal per org. Gross adjustments are billed once for the whole invoice, so
     * the per-event entries omit them and an event-scoped table has to read them from here.
     */
    export function invoiceSubtotalByOrg(invoice: Invoice | undefined): Map<number, AccountSubTotal> {
        const byOrg = new Map<number, AccountSubTotal>();

        for (const account of invoice?.billing?.subtotals?.[0]?.account ?? []) {
            const orgId = account.chargeToId;
            if (!orgId) continue;

            const charged = byOrg.get(orgId);
            byOrg.set(orgId, charged ? foldAccounts(charged, account) : account);
        }
        return byOrg;
    }

    /** Each org's invoice number, as the invoice-wide account entry it is billed on reports it. */
    export function invoiceNumbersByOrg(invoice: Invoice | undefined): Map<number, string> {
        const byOrg = new Map<number, string>();

        for (const [orgId, account] of invoiceSubtotalByOrg(invoice)) {
            byOrg.set(orgId, account.invoiceId ?? "");
        }
        return byOrg;
    }

    /**
     * One org's gross adjustments. They are billed once for the whole invoice, so the per-event
     * `accountEvent` entries omit them; an event-scoped table reads them off the invoice-wide
     * `account` entry instead.
     */
    export function orgGrossAdjustments(
        totals: OrgTotals,
        invoiceSubtotal: AccountSubTotal | undefined,
        combineRelated: boolean,
    ): number {
        return combineRelated ? totals.grossAdjustments : (invoiceSubtotal?.grossAdjustments ?? 0);
    }

    /** One org's total charge, gross adjustments included. */
    export function orgGrandTotal(
        totals: OrgTotals,
        invoiceSubtotal: AccountSubTotal | undefined,
        combineRelated: boolean,
    ): number {
        return (
            totals.grandTotal || totals.eventsTotalCharge + orgGrossAdjustments(totals, invoiceSubtotal, combineRelated)
        );
    }

    export function adjustmentsByOrg(invoice: Invoice | undefined): Map<number, Adjustment[]> {
        const map = new Map<number, Adjustment[]>();
        for (const adjustment of invoice?.billing?.adjustments ?? []) {
            const orgId = adjustment.chargeToId;
            if (!orgId) continue;
            map.set(orgId, [...(map.get(orgId) ?? []), adjustment]);
        }
        return map;
    }

    /**
     * Whether a payment belongs to the event being priced. A payment with no event cannot be
     * attributed to one, so it counts either way.
     */
    export function isPaymentInScope(payment: IPaymentDetails, scope: InvoiceScope): boolean {
        return scope.combineRelated || !payment.eventId || payment.eventId === scope.eventId;
    }

    /** Dollars paid per org, summed in cents so a long list of payments cannot drift. */
    export function paymentsByOrg(invoice: Invoice | undefined, scope: InvoiceScope): Map<number, number> {
        const cents = new Map<number, number>();

        for (const payment of invoice?.payments ?? []) {
            const orgId = payment.organizationId;
            if (!orgId || payment.isVoid || payment.paymentStatus !== "paid") continue;
            if (!isPaymentInScope(payment, scope)) continue;
            const paid = S25PricingUtil.convertToCents((payment.amountTotalCents ?? 0) / 100);
            cents.set(orgId, (cents.get(orgId) ?? 0) + paid);
        }

        return new Map([...cents].map(([orgId, total]) => [orgId, S25PricingUtil.convertToDollars(total)]));
    }

    export type OrgFigures = { charge: number; paid: number; balance: number };

    /**
     * Each org's total charge, payments and outstanding balance. The balance is subtracted in cents
     * so neither figure drifts against the one beside it.
     */
    export function orgFiguresByOrg(
        invoice: Invoice | undefined,
        orgIds: Iterable<number>,
        scope: InvoiceScope,
    ): Map<number, OrgFigures> {
        const subtotals = subtotalsByOrg(invoice, scope),
            invoiceSubtotals = invoiceSubtotalByOrg(invoice),
            payments = paymentsByOrg(invoice, scope);

        return new Map(
            [...orgIds].map((orgId): [number, OrgFigures] => {
                const totals = sumSubtotals(subtotals.get(orgId) ?? []),
                    charge = orgGrandTotal(totals, invoiceSubtotals.get(orgId), scope.combineRelated),
                    paid = payments.get(orgId) ?? 0;

                return [
                    orgId,
                    {
                        charge,
                        paid,
                        balance: S25PricingUtil.convertToDollars(
                            S25PricingUtil.convertToCents(charge) - S25PricingUtil.convertToCents(paid),
                        ),
                    },
                ];
            }),
        );
    }

    /** Every tax the invoice charges, collapsed to one entry per tax name. */
    export function taxBreakdown(lineItems: LineItem[]): { taxName: string; taxCharge: number }[] {
        const byName = new Map<string, number>();

        for (const lineItem of lineItems) {
            for (const tax of lineItem.taxes ?? []) {
                byName.set(tax.taxName, (byName.get(tax.taxName) ?? 0) + (tax.taxCharge ?? 0));
            }
        }

        return [...byName].map(([taxName, taxCharge]) => ({ taxName, taxCharge }));
    }
}
